import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  Skeleton,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { AxiosError } from 'axios';
import { useParams } from 'react-router';
import { history } from 'features/common/history/history';
import { Header } from './components/Header';
import { contextIdsRoute } from 'features/context-ids/routes';
import { variants } from 'features/common/components/GenericErrorTemplate/resources';
import { StatusView } from 'features/common/components/StatusView';
import { ResourcesTable } from './components/ResourcesTable';
import { useEnhancedResources } from 'features/context-ids/hooks/useEnhancedResources';
import { useGetSitecoreContextId } from 'features/context-ids/api/useGetSitecoreContextId';
import { FilteredContextsTable } from './components/FilteredContextsTable';
import { Feature, useFeatures } from 'lib/featureFlags/features';
import { AddSearchResourceModal } from './components/AddSearchResourceModal';
import { USER_SEARCH_RESOURCE_ID } from 'features/context-ids/hooks/embeddedResources';
import { useGetAvailableProducts } from 'features/context-ids/api/useGetAvailableProducts';
import { env as envConfig } from 'features/common/config/envConfig';
import { useGetTenantsQuery } from 'gql/graphql';
import { getAvailableProductsArray } from 'features/context-ids/utils';
import { useUpsertContextProduct } from 'features/context-ids/api/useUpsertContextProduct';
import { getSyncWithMasterContextPlan } from './syncWithMasterContextPlan';
import { SyncWithMasterContextModal } from './components/SyncWithMasterContextModal';

const normalizeCode = (value?: string) =>
  (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const isStrictSearchCode = (value?: string) =>
  normalizeCode(value).includes('search');

export const ContextIdDetails = () => {
  const { sitecoreContextId } = useParams<{ sitecoreContextId: string }>();
  const { SYNC_WITH_MASTER_CONTEXT } = useFeatures();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isSyncModalOpen,
    onOpen: onSyncModalOpen,
    onClose: onSyncModalClose,
  } = useDisclosure();
  const toast = useToast();

  const { data } = useGetSitecoreContextId({ sitecoreContextId });
  const {
    name,
    description,
    type: sitecoreContextType,
    searchTenant: searchTenantId,
  } = data ?? {};
  const {
    resources: contextResources,
    updatedResources,
    isLoading,
    error,
  } = useEnhancedResources(sitecoreContextId);
  const {
    mutateAsync: upsertContextProductAsync,
    isLoading: isSyncWithMasterContextLoading,
  } = useUpsertContextProduct({ sitecoreContextId });

  const isCustomContext = sitecoreContextType === 'custom';

  const { data: availableProductsData } = useGetAvailableProducts({
    enabled: !!sitecoreContextId,
  });
  const { data: tenantsData } = useGetTenantsQuery({
    variables: {
      first: 100,
      env: envConfig,
    },
    skip: !sitecoreContextId,
  });
  const availableProducts = getAvailableProductsArray(
    availableProductsData?.data,
  );

  const hasSearchTenantFromAvailableProducts = availableProducts.some(
    (availableProduct) =>
      isStrictSearchCode(availableProduct.productCode) ||
      (availableProduct.productCodes ?? []).some(isStrictSearchCode),
  );

  const hasSearchTenantFromTenantInventory = (
    tenantsData?.user?.applications?.nodes ?? []
  ).some((tenantNode) => {
    if (isStrictSearchCode(tenantNode?.productCode)) return true;

    return (tenantNode?.labels ?? []).some(
      (label) =>
        (label?.key ?? '').toLowerCase() === 'product' &&
        isStrictSearchCode(label?.value),
    );
  });

  const hasOrgSearchProduct =
    !!searchTenantId ||
    hasSearchTenantFromAvailableProducts ||
    hasSearchTenantFromTenantInventory;

  const visibleResources = updatedResources.filter(
    (resource) => resource.isVisible,
  );

  const existingSearchTenantIds = useMemo(
    () =>
      [
        ...new Set(
          (contextResources ?? [])
            .filter(
              (resource) =>
                isStrictSearchCode(resource.type) ||
                isStrictSearchCode(resource.tenantProductCode),
            )
            .map((resource) => resource.tenantId)
            .filter(Boolean),
        ),
      ] as string[],
    [contextResources],
  );

  const canAddSearchResource = hasOrgSearchProduct;
  const syncPlan = useMemo(
    () => getSyncWithMasterContextPlan(contextResources),
    [contextResources],
  );

  const handleSyncConfirm = useCallback(async () => {
    const { target } = syncPlan;
    if (!target) return;

    try {
      await upsertContextProductAsync({
        productCode: 'XMCloud',
        tenantId: target.tenantId,
        environment: target.environment,
      });

      onSyncModalClose();

      toast({
        status: 'success',
        description: (
          <>
            SitecoreAI resources on <strong>{name}</strong> have been synced
            with the <strong>{target.environmentLabel}</strong> master Context
            ID for <strong>{target.tenantName}</strong>
          </>
        ),
      });
    } catch (error) {
      const errorDetail = (error as AxiosError<{ detail?: string }>)?.response
        ?.data?.detail;
      toast({
        status: 'error',
        description: errorDetail ?? 'Failed to sync with master Context ID.',
      });
    }
  }, [name, syncPlan, toast, upsertContextProductAsync, onSyncModalClose]);

  const hasUserManagedSearchResource = (contextResources ?? []).some(
    (resource) => resource.id === USER_SEARCH_RESOURCE_ID,
  );
  const isAddSearchResourceDisabled = hasUserManagedSearchResource;
  const addSearchResourceDisabledReason = isAddSearchResourceDisabled
    ? 'A Search resource already exists for this Context ID.'
    : undefined;

  if (error)
    return (
      <>
        <BreadcrumbMenu currentItem={sitecoreContextId} />
        <StatusView
          icon={variants['bad-request'].illustration.colored}
          iconAlt='alert icon'
          title='Failed to fetch this Context ID'
        />
      </>
    );

  return (
    <Flex flexDir='column' gap={10}>
      <BreadcrumbMenu currentItem={sitecoreContextId} />
      <Header
        contextId={sitecoreContextId}
        type={sitecoreContextType}
        onSyncWithMasterContext={
          SYNC_WITH_MASTER_CONTEXT && isCustomContext && syncPlan.target
            ? onSyncModalOpen
            : undefined
        }
        isSyncWithMasterContextLoading={isSyncWithMasterContextLoading}
      />
      <Flex data-testid='context-id-details-body' direction='column' gap={10}>
        <Flex direction='column' gap={2}>
          <ContextIdDetailsField
            label='Name'
            value={name}
            isLoading={isLoading}
            gap={14}
          />
          <ContextIdDetailsField
            label='Description'
            value={description}
            isLoading={isLoading}
            gap={4}
          />
        </Flex>
        <ResourcesTable
          resources={visibleResources}
          isLoading={isLoading}
          onAddSearchResource={canAddSearchResource ? onOpen : undefined}
          isAddSearchResourceDisabled={isAddSearchResourceDisabled}
          addSearchResourceDisabledReason={addSearchResourceDisabledReason}
        />
        <Feature name='SCOPED_CONTEXT_MAPPING'>
          <FilteredContextsTable resources={visibleResources} />
        </Feature>
      </Flex>
      {canAddSearchResource && (
        <AddSearchResourceModal
          isOpen={isOpen}
          onClose={onClose}
          sitecoreContextId={sitecoreContextId}
          existingSearchTenantIds={existingSearchTenantIds}
        />
      )}
      {SYNC_WITH_MASTER_CONTEXT && isCustomContext && syncPlan.target && (
        <SyncWithMasterContextModal
          isOpen={isSyncModalOpen}
          onClose={onSyncModalClose}
          onConfirm={handleSyncConfirm}
          target={syncPlan.target}
          isLoading={isSyncWithMasterContextLoading}
        />
      )}
    </Flex>
  );
};

const BreadcrumbMenu = ({ currentItem }: { currentItem: string }) => (
  <Breadcrumb data-testid='app-details-breadcrumb'>
    <BreadcrumbItem>
      <BreadcrumbLink
        onClick={() =>
          history.push({
            pathname: contextIdsRoute,
            search: history.location.search,
          })
        }
      >
        Context IDs
      </BreadcrumbLink>
    </BreadcrumbItem>
    <BreadcrumbItem isCurrentPage>
      <BreadcrumbLink href='#'>{currentItem}</BreadcrumbLink>
    </BreadcrumbItem>
  </Breadcrumb>
);

const ContextIdDetailsField = ({
  label,
  value,
  isLoading,
  gap,
}: {
  label: string;
  value?: string;
  isLoading: boolean;
  gap: number;
}) => {
  if (!value && !isLoading) return null;
  return isLoading ? (
    <Skeleton height={5} width='200px' />
  ) : (
    <Flex gap={gap}>
      <Text variant='subtle' fontWeight='bold'>
        {label}
      </Text>
      <Text>{value}</Text>
    </Flex>
  );
};
